import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useLanguage } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";
import { clearDailyCards } from "@/lib/dailyCard";

const DISCLAIMER_KEY = "arcana.disclaimer.accepted.v1";
const FIRSTNAME_KEY = "arcana.firstName.v1";
const FN_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ\-]{1,15}$/;

const HomePage = () => {
  const { t, language } = useLanguage();
  const nav = useNavigate();

  const [accepted, setAccepted] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const sync = () => {
      setAccepted(window.localStorage.getItem(DISCLAIMER_KEY) === "1");
      setFirstName(window.localStorage.getItem(FIRSTNAME_KEY) || "");
      setError("");
    };
    sync();
    clearDailyCards();
    // Header dispatches this when the user clicks "Changer de consultant(e)"
    window.addEventListener("arcana:consultant-cleared", sync);
    return () => window.removeEventListener("arcana:consultant-cleared", sync);
  }, []);

  const toggleAccept = (e) => {
    const v = e.target.checked;
    setAccepted(v);
    window.localStorage.setItem(DISCLAIMER_KEY, v ? "1" : "0");
  };

  // Validates name + notice, then routes to the chosen path ("/daily" or "/welcome").
  const go = (path) => {
    setError("");
    if (!FN_RE.test(firstName)) {
      setError(t.reading.firstNameError);
      document.querySelector('[data-testid="auth-register-first-name"]')?.focus();
      return;
    }
    if (!accepted) {
      toast.error(t.disclaimer.mustAccept);
      return;
    }
    window.localStorage.setItem(FIRSTNAME_KEY, firstName.trim());
    nav(path);
  };

  const choiceBtn =
    "inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full transition-colors duration-300 text-[0.75rem] tracking-[0.4em] uppercase font-semibold";

  return (
    <main className="relative min-h-[calc(100vh-72px)] max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
      {/* Logo + tagline */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center mb-14">
        <div className="md:col-span-6 flex justify-center md:justify-start">
          <motion.img
            initial={{ opacity: 0, scale: 0.92, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            src="/logo-am.png"
            alt="Arcana Mystique"
            data-testid="home-logo"
            className="w-64 h-64 md:w-80 md:h-80 rounded-full object-cover object-center border-2 border-gold/30 bg-oxblood-surface drop-shadow-[0_10px_40px_rgba(212,175,55,0.25)]"
          />
        </div>

        <div className="md:col-span-6" data-testid="home-pricing">
          <div className="rune text-sm tracking-[0.5em] uppercase text-gold/70 mb-4">
            ✦ ARCANA MYSTIQUE ✦
          </div>
          <motion.h1
            key={language}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif font-light text-4xl md:text-5xl leading-[1.05] text-ivory tracking-tight"
          >
            <span className="block">{t.tagline}</span>
            <span className="block italic shimmer-gold">{t.taglineLine2}</span>
          </motion.h1>
        </div>
      </section>

      {/* 1 · First name */}
      <section className="mb-10">
        <form
          onSubmit={(e) => e.preventDefault()}
          data-testid={HOME.inlineForm}
          className="rounded-md border border-gold/30 bg-oxblood-surface/60 p-6 md:p-8"
        >
          <label className="block text-sm md:text-base tracking-[0.35em] uppercase text-gold font-bold mb-3">
            ✦ {t.auth.firstNameLabel}
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value.slice(0, 15));
              setError("");
            }}
            maxLength={15}
            required
            autoFocus
            placeholder={t.reading.firstNamePlaceholder}
            data-testid="auth-register-first-name"
            className="w-full bg-oxblood-surface border border-gold/25 rounded-md px-4 py-3 font-serif text-ivory text-lg placeholder-ivory/30 focus:outline-none focus:border-gold/70 transition-colors duration-300"
          />
          <p className="mt-2 text-xs text-ivory/40 font-serif italic">{t.auth.firstNameHelp}</p>
          {error && (
            <p
              data-testid="home-first-name-error"
              className="mt-3 text-sm text-red-300 font-serif italic border-l-2 border-red-400/60 pl-3"
            >
              {error}
            </p>
          )}
        </form>
      </section>

      {/* 2 · The 78 arcana */}
      <section className="mb-10 max-w-3xl" data-testid="home-subhero">
        <p className="text-ivory/70 font-sans font-light leading-relaxed text-base md:text-lg">
          {t.subhero}
        </p>
      </section>

      {/* 3 · Notice with mandatory checkbox */}
      <section className="mb-10" data-testid="home-notice-section">
        <div className="rounded-md border border-gold/40 bg-oxblood-surface/70 p-6 md:p-8">
          <div className="text-sm md:text-base tracking-[0.4em] uppercase text-[#14103A] font-bold mb-3">
            ✧ {t.disclaimer.title}
          </div>
          <p className="text-ivory/80 font-serif italic leading-relaxed whitespace-pre-line">
            {t.disclaimer.body}
          </p>
          <label className="mt-6 flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={accepted}
              onChange={toggleAccept}
              data-testid={HOME.disclaimerCheckbox}
              className="mt-1 h-5 w-5 accent-gold cursor-pointer"
            />
            <span className="text-ivory font-sans font-bold text-base md:text-lg group-hover:text-gold transition-colors duration-300">
              {t.disclaimer.accept}
            </span>
          </label>
        </div>
      </section>

      {/* 4 · Choice: card of the day or five-card reading */}
      <section className="mb-20" data-testid="home-choice-section">
        <div className="rounded-md border border-gold/30 bg-oxblood-surface/60 p-6 md:p-8">
          <div className="text-sm md:text-base tracking-[0.35em] uppercase text-gold font-bold mb-5">
            ✦ {t.home.chooseTitle}
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => go("/daily")}
              disabled={!accepted}
              data-testid={HOME.dailyBtn}
              className={`${choiceBtn} ${
                accepted
                  ? "border border-gold text-gold hover:bg-gold hover:text-oxblood"
                  : "border border-gold/30 text-gold/40 cursor-not-allowed"
              }`}
            >
              ✧ {t.home.dailyBtn}
            </button>
            <button
              type="button"
              onClick={() => go("/welcome")}
              disabled={!accepted}
              data-testid={HOME.readingBtn}
              className={`${choiceBtn} ${
                accepted ? "bg-gold text-oxblood hover:bg-gold-soft" : "bg-gold/30 text-oxblood/50 cursor-not-allowed"
              }`}
            >
              {t.home.readingBtn} →
            </button>
          </div>
          {!accepted && (
            <p className="mt-4 text-sm md:text-base text-ivory font-sans font-bold">
              {t.disclaimer.mustAccept}
            </p>
          )}
        </div>
      </section>

      <div className="mt-16 pt-8 border-t border-gold/10 text-center">
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ivory/40">{t.footer}</p>
      </div>
    </main>
  );
};

export default HomePage;
